import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Search } from "@mui/icons-material";
import {
    DataGrid,
    GridFilterModel, GridSortModel, GridLinkOperator, GridRowId
} from "@mui/x-data-grid";
import { useAuth } from "../../_auth/useAuth";
import {
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, ListViewToolbar,
    DialogBox, IDialogTextProps,
    MessageBox, MsgBoxType, IMessagePanelProps,
    MuiGridStateProp, MuiGridDataProp, muiGridFilterIsValid, FormTitle,
    BrokerAddDialog, IAutocompleteDialogTextProps,
} from "../_shared";
import { IItemIdsRequest, IMuiDataRequest, brokerApi } from "../../_api";
import { AuthRolesEnum, brokerViewColumns } from "../../_appData";

export const BrokerListView: React.FC = () => {
    const { isAuthenticated, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const getGridDataRef = useRef<() => Promise<void>>();
    const [dlgBoxOpen, setDlgBoxOpen] = React.useState<boolean>(false);
    const [dlgBoxProps, setDlgBoxProps] = useState<IDialogTextProps>({ message: "" });
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridState, setGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [{ field: 'modify_datetime', sort: 'desc' }],
    });
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });
    const [dlgSelBrokerOpen, setDlgSelBrokerOpen] = useState<boolean>(false);
    const [dlgSelBrokerProps, setDlgSelBrokerProps] = useState<IAutocompleteDialogTextProps>({ title: "Choose Broker / Agent / List Admin" });

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsLoading(true);

        const options: IItemIdsRequest = { item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        brokerApi.remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Call getGridData via ref to refresh the grid
                        if (getGridDataRef.current) {
                            getGridDataRef.current();
                        }
                        msgBox_Show(
                            `${resp.count} Broker items have been deleted...`,
                            undefined,
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting Broker data... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete Broker data...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [msgBox_Show, getAccessToken]);

    const dlgBox_Close = useCallback((value?: string) => {
        const selRows = selectionModel;
        setDlgBoxOpen(false);

        if (value && value === "ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }, [selectionModel, deleteSelectedItems]);

    const dlgBox_Show = (message: string, title?: string) => {
        setDlgBoxProps({
            message: message,
            title: title,
        })
        setDlgBoxOpen(true);
    }

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const onGridPageChange = (page: number) => {
        setGridState((prev) => ({
            ...prev,
            page: page
        }));
    };

    const onGridPageSizeChange = (size: number) => {
        setGridState((prev) => ({
            ...prev,
            page_size: size
        }));
    };

    const onGridFilterChange = (model: GridFilterModel) => {
        if (muiGridFilterIsValid(model)) {
            setGridState((prev) => ({
                ...prev,
                filters: model
            }));
        }
    };

    const onGridSortChange = (model: GridSortModel) => {
        setGridState((prev) => ({
            ...prev,
            sorts: model
        }));
    };

    const onClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        navigate("/listadmin/new");
    };

    const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if (selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be edited",
                "Edit Broker...",
                "warning"
            );
        if (selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be edited",
                "Edit Broker...",
                "warning"
            );

        if (selRows.length === 1) {
            navigate(`/listadmin/${selRows[0]}`);
        }
    };

    const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgBox_close for processing of dialog result
        dlgBox_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const execSearch = (term: string) => {
        // Modify Grid Filter model
        let model: GridFilterModel = {
            items: [],
            linkOperator: GridLinkOperator.Or
        }
        // Search Broker_SId, CompanyName
        if (term.length > 0) {
            model.items.push({ id: 1001, columnField: "listadmin_sid", operatorValue: "contains", value: term });
            model.items.push({ id: 1002, columnField: "company__company_name", operatorValue: "contains", value: term });
        }

        setGridState((prev) => ({
            ...prev,
            filters: model
        }));
    };

    const onClickReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        setGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    };

    const onClickBrokerSelect = (e: React.MouseEvent<HTMLButtonElement>) => {
        setDlgSelBrokerProps({
            title: "Choose Authorized Broker",
        });
        setDlgSelBrokerOpen(true);
    };

    const dlgSelBroker_Close = (value?: string, item_id?: number) => {
        setDlgSelBrokerOpen(false);
        if (value && value === "ok" && item_id) {
            // Navigate to broker view in partial edit mode from listadmin dashboard
            // Only contact tab will be editable, other tabs in view mode
            navigate(`/listadmin/${item_id}`, {
                state: {
                    mode: 'partial-edit',
                    fromBrokerDashboard: true
                }
            });
        }
    };

    const handleCreateBroker = () => {
        // Navigate to broker creation with listadmin context
        navigate('/listadmin/new', {
            state: {
                brokerCreationContext: 'listadmin'
            }
        });
    };

    const getGridData = useCallback(async () => {
        setIsLoading(true);

        const options: IMuiDataRequest = gridState;
        const auth_token: string = await getAccessToken();

        brokerApi.get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Flatten company fields to top-level for grid
                        const rows = (resp.data || []).map((row: any) => {
                            // Use 'title' from top-level response for contact title
                            const contact_title = row.title || '';
                            if (row.company) {
                                return {
                                    ...row,
                                    ...row.company,
                                    ...row.user,
                                    representative_title: contact_title,
                                    title: contact_title,
                                };
                            }
                            return row;
                        });
                        setGridData({
                            rows,
                            row_count: resp.count!
                        });
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Broker data... \n" +
                            "please retry your last action. If you continue to experience issues, " +
                            "please contact customer support.",
                            "Unable to Load Broker data...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [gridState, getAccessToken, msgBox_Show]);

    // Store getGridData in ref so it can be called from deleteSelectedItems
    useEffect(() => {
        getGridDataRef.current = getGridData;
    }, [getGridData]);

    // Track previous values to prevent unnecessary API calls
    const prevValuesRef = useRef<string>('');

    useEffect(() => {
        // Create a stable key from the values that matter
        const currentKey = JSON.stringify({
            page: gridState.page,
            page_size: gridState.page_size,
            filters: gridState.filters,
            sorts: gridState.sorts
        });

        // Only call API if values actually changed
        if (prevValuesRef.current !== currentKey) {
            prevValuesRef.current = currentKey;
            getGridData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridState.page, gridState.page_size, gridState.filters, gridState.sorts]);

    const lvGridToolbar = () => {
        const isAdmin = userHasPrivilege([AuthRolesEnum.ListOwner]);
        const isLicensee = userHasPrivilege([AuthRolesEnum.Licensee]);
        const isROUser = userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.Broker, AuthRolesEnum.PafUser]);
        return (
            <ListViewToolbar
                onClickNew={onClickNew}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickReset={onClickReset}
                execSearch={execSearch}
                canView={isAdmin || isROUser}
                canAdd={isAdmin || isLicensee}
                canUpdate={isAdmin || isLicensee}
                canDelete={isLicensee}
                canSearch={true}
                showNewButton={false}
            />
        );
    }

    return (
        <>
            <Box
                sx={{
                    paddingTop: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: { xs: 'flex-start', sm: 'space-between' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        mb: 2,
                        gap: { xs: 1.5, sm: 0 },
                    }}
                >
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <FormTitle
                            title={"Broker Agents"}
                            titleIcon={"group_work_rounded_icon"}
                            typeVariant={"h4"}
                        />
                    </Box>
                       { !userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.Broker, AuthRolesEnum.ListAdmin]) && <Button
                            variant="contained"
                            startIcon={<Search />}
                            onClick={onClickBrokerSelect}
                            sx={{
                                minWidth: { xs: '100%', sm: 168 },
                                textTransform: 'none',
                                fontWeight: 500,
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Search & Add Broker
                        </Button>
                        }
                </Box>

                {isAuthenticated ? (
                    <Box sx={{ marginTop: 2 }}>
                        <DaGridContainer
                            sx={{
                                height: 600,
                                width: '100%',
                            }}
                        >
                            <DataGrid
                                components={{
                                    LoadingOverlay: DaGridLoadingOverlay,
                                    Toolbar: lvGridToolbar,
                                }}
                                loading={isLoading}
                                columns={brokerViewColumns}
                                rows={gridData.rows}
                                rowCount={gridData.row_count}
                                getRowId={(row) => row.listadmin_id}
                                filterMode="server"
                                paginationMode="server"
                                sortingMode="server"
                                pagination
                                page={gridState.page}
                                pageSize={gridState.page_size}
                                onFilterModelChange={onGridFilterChange}
                                onSortModelChange={onGridSortChange}
                                onPageChange={onGridPageChange}
                                onPageSizeChange={onGridPageSizeChange}
                                rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                headerHeight={DaGridSettings.headerRowHeight}
                                rowHeight={DaGridSettings.rowHeight}
                                checkboxSelection
                                disableSelectionOnClick
                                selectionModel={selectionModel}
                                onSelectionModelChange={(newSelection) => {
                                    setSelectionModel(newSelection);
                                }}
                            />
                        </DaGridContainer>
                    </Box>
                ) : (
                    <Typography variant="h5">Please Login to use this system...</Typography>
                )}
            </Box>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgBoxOpen}
                onClose={dlgBox_Close}
                {...dlgBoxProps}
            />
            {/* <CustomerAddDialog
                isOpen={dlgSelBrokerOpen}
                onClose={dlgSelBroker_Close}
                userId={-1}
                onCreateBroker={handleCreateBroker}
                isBrokerRequested={true}
                {...dlgSelBrokerProps}
            /> */}
            <BrokerAddDialog
                isOpen={dlgSelBrokerOpen}
                onClose={dlgSelBroker_Close}
                userId={-1}
                onCreateBroker={handleCreateBroker}
                isBrokerRequested={true}
                {...dlgSelBrokerProps}
            />
        </>
    );
}
